/**
 * CAOSLAB.DEV — INTERACTIVE CHAOS-TO-GROWTH CANVAS ENGINE
 * High-performance 60fps particle physics simulation:
 * Translates turbulent chaotic digital noise into structured golden growth vectors.
 */

export function initChaosCanvas() {
  const canvas = document.getElementById('chaos-canvas');
  if (!canvas) return;

  const ctx = canvas.getContext('2d', { alpha: true });
  let width, height;
  let animationFrameId;

  // Mouse & Scroll State
  const mouse = {
    x: window.innerWidth / 2,
    y: window.innerHeight / 3,
    targetX: window.innerWidth / 2,
    targetY: window.innerHeight / 3,
    radius: 180,
    isHovering: false
  };

  // Particle configuration
  const isMobile = window.innerWidth < 768;
  const PARTICLE_COUNT = isMobile ? 65 : 140;
  const particles = [];

  const COLORS = [
    { r: 212, g: 175, b: 55, a: 0.8 },  // Liquid Gold
    { r: 247, g: 224, b: 125, a: 0.9 }, // Pale Gold Highlight
    { r: 62,  g: 13,  b: 98,  a: 0.7 }, // Deep Tech Purple
    { r: 142, g: 36,  b: 170, a: 0.8 }, // Vibrant Purple
    { r: 255, g: 255, b: 255, a: 0.6 }  // Pure Spark White
  ];

  class Particle {
    constructor() {
      this.reset(true);
    }

    reset(initial = false) {
      this.x = Math.random() * width;
      this.y = initial ? Math.random() * height : (Math.random() < 0.5 ? -10 : height + 10);
      
      // Chaotic velocity
      this.vx = (Math.random() - 0.5) * 1.2;
      this.vy = (Math.random() - 0.5) * 1.2 - 0.3; // Slight upward flow (growth)
      
      this.size = Math.random() * 2.5 + 1.2;
      this.baseSize = this.size;
      this.color = COLORS[Math.floor(Math.random() * COLORS.length)];
      this.alpha = Math.random() * 0.7 + 0.3;
      
      // Oscillation phase
      this.angle = Math.random() * Math.PI * 2;
      this.spinSpeed = (Math.random() - 0.5) * 0.03;
      this.orbitRadius = Math.random() * 120 + 30;
      
      // Order vs Chaos factor (0 = pure chaos, 1 = structured growth)
      this.orderFactor = Math.random();
    }

    update() {
      this.angle += this.spinSpeed;

      // Distance to mouse pointer
      const dx = mouse.x - this.x;
      const dy = mouse.y - this.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < mouse.radius && mouse.isHovering) {
        // Gravitational vortex pull when near mouse
        const force = (1 - dist / mouse.radius) * 0.08;
        const angleToMouse = Math.atan2(dy, dx);
        
        // Swirling vortex physics
        this.vx += Math.cos(angleToMouse + Math.PI / 2) * force * 2.5;
        this.vy += Math.sin(angleToMouse + Math.PI / 2) * force * 2.5;
        
        // Attract toward center
        this.vx += Math.cos(angleToMouse) * force * 1.5;
        this.vy += Math.sin(angleToMouse) * force * 1.5;
      } else {
        // Organic turbulence flow
        this.vx += Math.cos(this.angle) * 0.02;
        this.vy += Math.sin(this.angle) * 0.02 - 0.01;
      }

      // Drag friction
      this.vx *= 0.96;
      this.vy *= 0.96;

      this.x += this.vx;
      this.y += this.vy;

      // Screen wrap
      if (this.x < -30) this.x = width + 30;
      if (this.x > width + 30) this.x = -30;
      if (this.y < -30) this.y = height + 30;
      if (this.y > height + 30) this.y = -30;
    }

    draw() {
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, ${this.alpha})`;
      ctx.shadowColor = `rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, 0.8)`;
      ctx.shadowBlur = 8;
      ctx.fill();
      ctx.shadowBlur = 0; // reset shadow for performance
    }
  }

  function resize() {
    width = canvas.width = window.innerWidth;
    height = canvas.height = window.innerHeight;
  }

  function initParticles() {
    particles.length = 0;
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      particles.push(new Particle());
    }
  }

  function drawConnections() {
    const maxDistance = isMobile ? 85 : 120;
    
    for (let i = 0; i < particles.length; i++) {
      for (let j = i + 1; j < particles.length; j++) {
        const p1 = particles[i];
        const p2 = particles[j];
        const dx = p1.x - p2.x;
        const dy = p1.y - p2.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < maxDistance) {
          const alpha = (1 - dist / maxDistance) * 0.22;
          ctx.beginPath();
          ctx.moveTo(p1.x, p1.y);
          ctx.lineTo(p2.x, p2.y);
          
          // Metallic gold & purple gradient lines
          const grad = ctx.createLinearGradient(p1.x, p1.y, p2.x, p2.y);
          grad.addColorStop(0, `rgba(${p1.color.r}, ${p1.color.g}, ${p1.color.b}, ${alpha})`);
          grad.addColorStop(1, `rgba(${p2.color.r}, ${p2.color.g}, ${p2.color.b}, ${alpha})`);
          
          ctx.strokeStyle = grad;
          ctx.lineWidth = 0.8;
          ctx.stroke();
        }
      }
    }
  }

  let isPaused = false;

  function animate() {
    if (isPaused) return;

    // Smooth lerp mouse position
    mouse.x += (mouse.targetX - mouse.x) * 0.08;
    mouse.y += (mouse.targetY - mouse.y) * 0.08;

    ctx.clearRect(0, 0, width, height);

    // Draw particle network
    for (let i = 0; i < particles.length; i++) {
      particles[i].update();
      particles[i].draw();
    }

    drawConnections();

    animationFrameId = requestAnimationFrame(animate);
  }

  // Handle visibility change to save CPU & prevent frame drops
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      isPaused = true;
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    } else {
      if (isPaused) {
        isPaused = false;
        animationFrameId = requestAnimationFrame(animate);
      }
    }
  }, { passive: true });

  // Event Listeners with passive optimization
  let resizeTimeout;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      resize();
      initParticles();
    }, 150);
  }, { passive: true });

  window.addEventListener('mousemove', (e) => {
    mouse.targetX = e.clientX;
    mouse.targetY = e.clientY;
    mouse.isHovering = true;
  }, { passive: true });

  window.addEventListener('mouseleave', () => {
    mouse.isHovering = false;
  }, { passive: true });

  // Touch Support
  window.addEventListener('touchmove', (e) => {
    if (e.touches.length > 0) {
      mouse.targetX = e.touches[0].clientX;
      mouse.targetY = e.touches[0].clientY;
      mouse.isHovering = true;
    }
  }, { passive: true });

  window.addEventListener('touchend', () => {
    mouse.isHovering = false;
  }, { passive: true });

  // Initialize
  resize();
  initParticles();
  animate();

  return () => {
    if (animationFrameId) cancelAnimationFrame(animationFrameId);
  };
}
